(ns frontend.ui.page.lp-portal.report.util
  (:require
   [common.util :as cu]
   [frontend.ui.style.theme.core :as theme :refer [color]]
   [garden.color :as gcolor]
   [goog.string :as gstring]
   [frontend.utils :as fu])
  (:import
   (goog.i18n NumberFormat)
   (goog.i18n.NumberFormat Format)))

(defn- gradient
  [pct from to]
  (int (+ from (* pct (- to from)))))

(defn- color-gradient
  [hex-from hex-to pct]
  (let [from (gcolor/hex->rgb hex-from)
        to (gcolor/hex->rgb hex-to)
        r (gradient pct (:red from) (:red to))
        g (gradient pct (:green from) (:green to))
        b (gradient pct (:blue from) (:blue to))]
    (gcolor/rgb->hex (gcolor/rgb r g b))))

(defn gradient-colors
  [from to step-count]
  (let [from-color (color from)
        to-color (color to)
        color-list (cond
                     (or (nil? from-color) (nil? to-color))
                     (do
                       (js/console.warn "Unknown color : " from " or " to)
                       ["#000"])
                     (<= step-count 1)                  ;; avoid divide by zero
                     [from-color]
                     (= step-count 2)                   ;; this works with logic below as well, but is faster
                     [from-color to-color]
                     :else
                     (let [step-increase (/ 1 (dec step-count))]
                       (->> (range step-count)
                            (mapv
                             (fn [idx]
                               (color-gradient from-color to-color (* idx step-increase)))))))]
    color-list))

(defn custom-colors
  []
  (theme/palette-value :custom-colors))

(defn resi-custom-colors
  []
  (theme/palette-value :resi-custom-colors))

(defn up-down-colors
  []
  [(color [:palette :brand-1])
   (color [:palette :danger])])

(def nff
  (NumberFormat. Format/DECIMAL))

(defn ->int-million
  [f]
  (if-not (number? f)
    (str f)
    (gstring/format "%.1f" (fu/round (/ f 1e6) 1))))

(defn ->int-thousand
  [f]
  (if-not (number? f)
    (str f)
    (gstring/format "%.1f" (fu/round (/ f 1e3) 1))))

(defn ->int
  ([suffix f]
   (if-not (number? f)
     (str f)
     (.format nff (when f (gstring/format (str "%i " suffix) (fu/round f 0))))))
  ([f]
   (->int nil f)))

(defn ->float
  ([suffix f]
   (if-not (number? f)
     (str f)
     (gstring/format (str "%.0f " suffix) (fu/round f 0))))
  ([f]
   (->float nil f)))

(defn ->float-x-decimals
  ([suffix f decimal-amount]
   (if-not (number? f)
     (str f)
     (gstring/format (str "%." decimal-amount "f" suffix) (fu/round f decimal-amount))))
  ([f decimal-amount]
   (->float-x-decimals nil f decimal-amount)))

(defn ->float-1-decimals
  ([suffix f]
   (if-not (number? f)
     (str f)
     (gstring/format (str "%.1f" suffix) (fu/round f 1))))
  ([f]
   (->float-1-decimals nil f)))

(defn ->float-4-decimals
  ([suffix f]
   (if-not (number? f)
     (str f)
     (gstring/format (str "%.4f" suffix) (fu/round f 4))))
  ([f]
   (->float-4-decimals nil f)))

(defn ->float-2-decimals
  ([suffix f]
   (if-not (number? f)
     (str f)
     (gstring/format (str "%.2f" suffix) (fu/round f 2))))
  ([f]
   (->float-2-decimals nil f)))

(defn ->multiple
  [f]
  (if-not (number? f)
    (str f)
    (gstring/format "%.1fx" (fu/round f 1))))

(defn ->multiple-without-x
  [f]
  (if-not (number? f)
    (str f)
    (gstring/format "%.1f" (fu/round f 1))))

(defn ->percent
  [f]
  (if-not (number? f)
    (str f)
    (gstring/format "%i%" (fu/round (* f 100) 2))))
;; TODO - round normal rounding, this clips 1 decimal without rounding

(defn ->percent-0-decimal
  [f]
  (if-not (number? f)
    (str f)
    (let [rounded (fu/round (* f 100) 0)]
      (gstring/format "%.0f%" rounded))))

(defn ->percent-1-decimal
  [f]
  (if-not (number? f)
    (str f)
    (let [rounded (fu/round (* f 100) 1)]
      (gstring/format "%.1f%" rounded))))

(defn ->percent-2-decimal
  [f]
  (if-not (number? f)
    (str f)
    (let [rounded (fu/round (* f 100) 2)]
      (gstring/format "%.2f%" rounded))))

(defn ->percent-2-decimals
  [f]
  (if-not (number? f)
    (str f)
    (gstring/format "%.2f%" (fu/round (* f 100) 2))))

(defn -percent-4-decimals
  [f]
  (if-not (number? f)
    (str f)
    (gstring/format "%.4f%" (fu/round (* f 100) 4))))

(def ^:private int->month
  {0  "Jan"
   1  "Feb"
   2  "Mar"
   3  "Apr"
   4  "May"
   5  "Jun"
   6  "Jul"
   7  "Aug"
   8  "Sep"
   9  "Oct"
   10 "Nov"
   11 "Dec"})

(defn ->date-MMM-yy
  [epoch-millis]
  (when (number? epoch-millis)
    (let [date (js/Date. epoch-millis)
          month (int->month (.getMonth date))
          year (-> (.getFullYear date)
                   str
                   (subs 2))]
      (str month "-" year))))

(defn ->date-dd-MM-yyyy
  [epoch-millis]
  (when (number? epoch-millis)
    (let [date (js/Date. epoch-millis)
          day (.getDate date)
          month (inc (.getMonth date))
          year (-> (.getFullYear date)
                   str)]
      (str day "." month "." year))))

(defn ->date-yyyy
  [epoch]
  (when (number? epoch)
    (-> epoch
        (js/Date.)
        (.getFullYear))))

(defn ->quarter
  [epoch]
  (when (number? epoch)
    (let [quarter-number (.floor js/Math (/ (+ (.getMonth (js/Date. epoch)) 3) 3))]
      (str quarter-number
           (case quarter-number
             1 "st"
             2 "nd"
             3 "rd"
             4 "th")
           " quarter"))))

;; FIXME factorize with fund-data
(defn transpose
  [v]
  (if (empty? v)
    v
    (apply mapv vector v)))

(defn ->array-of-tuples
  [{:keys [labels] :as values} k]
  (map (fn [label value] [label value])
       labels
       (get values k)))

(defn ->data-for-table
  "Take a map of values exported from a LP Portal Excel file and arrange them as per `specifications`
  to generate a list of rows without any UI considerations. The result of this function is a tuple
  consisting of the list of rows and a list of UI transformation ready to be sent to a UI (web, PDF
  or anything else). Note that if specifying `:transpose true`, no UI transformation should be
  provided in the `specifications`.

  This function also takes optional parameters:
  - `:title` to specify a title to the table (row 0, cell 0)
  - `:transpose?` whether to transpose the list of rows (default: `false`). This is in direct
    correlation with how we read data from Excel files in the LP Portal (see
  `read-row-growing-table`)"
  [{:keys [labels] :as values}
   specifications
   & {:keys [title transpose?]}]
  (let [rows-with-labels (map (fn [[k row-label transformation]]
                                (->> (get values k)
                                     (map (or transformation identity))
                                     (cons row-label)))
                              specifications)
        first-row (cons title labels)
        data (cons first-row rows-with-labels)
        ui-transformations (map #(nth % 3 nil) specifications)
        transf (if transpose?
                 (->> data
                      (transpose)
                      (map-indexed (fn [_idx _]
                                     (first ui-transformations)))
                      (mapv identity))
                 ui-transformations)]
    ;; TODO once refactoring is done, make this fn return {:data … :transformations …}
    [(if transpose?
       (transpose data)
       data)
     transf]))

(defn resolve-automatic-divider
  [f]
  (cond
    (<= f 99)
    1
    (<= f 99999)
    1000
    :else
    1000000))

(defn format-with-automatic-multiplier
  [f decimal-amount suffix]
  (let [divider (resolve-automatic-divider f)
        multiplier-suffix (case divider
                 1 ""
                 1000 "k"
                 1000000 "M"
                 "")]
    (gstring/format (str "%." decimal-amount "f" multiplier-suffix suffix) (fu/round (/ f divider) decimal-amount))))

(defn editable-y-axis-formatter
  ([suffix f decimal-amount divider]
   (cond
     (and (= "auto" divider) (number? f) (number? decimal-amount))
     (format-with-automatic-multiplier f decimal-amount suffix)
     (and (= "auto" divider) (number? f))
     (format-with-automatic-multiplier f 0 suffix)
     (and (number? f) (number? decimal-amount) (string? suffix))
     (gstring/format (str "%." decimal-amount "f" suffix) (fu/round (/ f divider) decimal-amount))
     (and (number? f) (string? suffix))
     (gstring/format (str "%." 0 "f" suffix) (fu/round (/ f divider) decimal-amount))
     :else
     (str f)))
  ([f decimal-amount divider]
   (editable-y-axis-formatter nil f decimal-amount divider)))

(defn blank-value->empty-indicator-str
  [empty-indicator cell-value]
  (if (cu/blank-value? cell-value)
    empty-indicator
    cell-value))
